import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AddressSearchResult,
  useGoogleApiAddressSearchLoader,
} from '@/experiences/sales-portal/components/address-search-input/use-google-api-address-search-loader';
import {
  Button,
  IconButton,
  Icons,
  SidePanel,
  Typography,
} from '@usepepper/sprout-kit-ui';
import {
  EditableAccountData,
  useEditableAccountLoader,
} from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/loaders/use-editable-account-loader';
import {
  EditAccountForm,
  EditAccountFormErrors,
} from '@/experiences/sales-portal/domain/accounts/screens/account-detail/containers/edit-account-panel/components/edit-account-form';
import { EditAccountFormSkeleton } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/containers/edit-account-panel/components/edit-account-form-skeleton';
import {
  EditAccountFormValues,
  useEditAccountMutator,
} from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/mutators/use-edit-account-mutator';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useGoogleApiAddressDetailsLoader } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/containers/edit-account-panel/hooks/use-google-api-address-details-loader';
import { useTheme } from '@/contexts/theme-context';

interface EditAccountPanelProps {
  onAccountSave?: () => void;
  onClose: () => void;
  restaurantUUID: string;
  supplierUUID: string;
}

export const EditAccountPanel: React.FC<EditAccountPanelProps> = ({
  onAccountSave,
  onClose,
  restaurantUUID,
  supplierUUID,
}) => {
  const theme = useTheme();
  const { showErrorToast, showToast } = useExperienceToasts();

  const [formValues, setFormValues] = useState<EditAccountFormValues>({
    accountId: '',
    address: '',
    address2: '',
    name: '',
    note: '',
    routeStop: '',
  });
  const [errors, setErrors] = useState<EditAccountFormErrors>({});

  const hydrateEditableAccountForm = useCallback(
    (data: EditableAccountData) => {
      setFormValues({
        accountId: data.accountId,
        address: data.address,
        address2: data.address2,
        name: data.name,
        note: data.note,
        routeStop: data.routeStop,
      });
    },
    [],
  );
  const {
    data: loadedAccountData,
    loading: accountDataLoading,
    refetch: refetchAccountData,
  } = useEditableAccountLoader({
    onCompleted: hydrateEditableAccountForm,
    restaurantUUID,
    supplierUUID,
  });

  const { addressResults, clear, isSearching, search, lastSearched } =
    useGoogleApiAddressSearchLoader();
  const handleAddressSearch = useCallback(
    (text: string) => {
      setFormValues((prev) => ({ ...prev, address: text }));
      search(text);
    },
    [search],
  );

  const { getAddressDetails } = useGoogleApiAddressDetailsLoader();
  const handleAddressSelect = useCallback(
    async (address: AddressSearchResult) => {
      const addressDetails = await getAddressDetails(address.placeId);
      if (addressDetails) {
        setFormValues((prev) => ({
          ...prev,
          address: addressDetails.address,
          address2: addressDetails.address2 || '',
        }));
      }
      clear();
    },
    [clear, getAddressDetails],
  );

  const onEditAccount = useCallback(
    <T extends keyof EditAccountFormValues>(
      field: T,
      value: EditAccountFormValues[T],
    ) => {
      setFormValues((prev) => ({
        ...prev,
        [field]: value,
      }));
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    },
    [errors],
  );

  const validateForm = useCallback(
    (values: EditAccountFormValues): EditAccountFormErrors => {
      const validationErrors: EditAccountFormErrors = {};

      if (!values.name.trim()) {
        validationErrors.name = i18n.t(
          'sales_portal.edit_account.validation.name_required',
        );
      }

      if (!values.address.trim()) {
        validationErrors.address = i18n.t(
          'sales_portal.edit_account.validation.address_required',
        );
      }

      return validationErrors;
    },
    [],
  );

  const isDirty = useMemo(() => {
    if (!loadedAccountData) {
      return false;
    }

    return (
      formValues.accountId !== loadedAccountData.accountId ||
      formValues.address !== loadedAccountData.address ||
      formValues.address2 !== loadedAccountData.address2 ||
      formValues.name !== loadedAccountData.name ||
      formValues.note !== loadedAccountData.note ||
      formValues.routeStop !== loadedAccountData.routeStop
    );
  }, [formValues, loadedAccountData]);

  const hasNoAddressSearchResults = () => {
    if (isSearching || !lastSearched || lastSearched.length < 1) {
      return false;
    }

    const hasNewSearchAddress = loadedAccountData?.address !== lastSearched;
    const noResults = addressResults.length === 0;

    return hasNewSearchAddress && noResults;
  };

  // real-time local form validation
  useEffect(() => {
    const validationErrors = validateForm(formValues);
    setErrors(validationErrors);
  }, [formValues, setErrors, validateForm]);

  const hasValidationErrors = useMemo(() => {
    return Object.keys(errors).length > 0;
  }, [errors]);

  const onAccountSaveError = useCallback(
    (error: Error) => {
      showErrorToast(
        error.message || i18n.t('sales_portal.edit_account.error'),
      );
    },
    [showErrorToast],
  );

  const onAccountSaveSuccess = useCallback(async () => {
    showToast({
      title: i18n.t('sales_portal.edit_account.success'),
      variant: 'success',
    });
    if (onAccountSave) {
      await onAccountSave();
    }
    refetchAccountData();
    onClose();
  }, [onAccountSave, onClose, refetchAccountData, showToast]);

  const { updateAccount, loading: accountDataSaving } = useEditAccountMutator({
    onError: onAccountSaveError,
    onSuccess: onAccountSaveSuccess,
  });

  const handleSave = useCallback(async () => {
    const validationErrors = validateForm(formValues);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    await updateAccount({
      formValues,
      restaurantUUID,
      supplierUUID,
    });
  }, [formValues, restaurantUUID, supplierUUID, updateAccount, validateForm]);

  const saveDisabled =
    hasValidationErrors || accountDataSaving || accountDataLoading || !isDirty;

  return (
    <>
      <SidePanel.Header
        leadingActions={
          <IconButton
            aria-label={i18n.t('sales_portal.side_panel.close')}
            icon={<Icons.X color={theme.sk.color.icon.primary} />}
            onPress={onClose}
            shape="rounded"
            size="$sm"
            variant="tertiary"
          />
        }
        title={
          <Typography size="$xs" variant="heading">
            {i18n.t('sales_portal.edit_account.title')}
          </Typography>
        }
      />
      <SidePanel.Body
        accessibilityLabel={i18n.t('sales_portal.edit_account.title')}
        gap={theme.sk.spacing.spaceLarge}
        role="dialog"
      >
        {accountDataLoading ? (
          <EditAccountFormSkeleton />
        ) : (
          <EditAccountForm
            addressSearchLoading={isSearching}
            addressSearchResults={addressResults}
            errors={errors}
            hasNoAddressSearchResults={hasNoAddressSearchResults()}
            onAddressSearch={handleAddressSearch}
            onAddressSearchClose={() => clear()}
            onAddressSelect={handleAddressSelect}
            onEditAccount={onEditAccount}
            values={formValues}
          />
        )}
      </SidePanel.Body>
      <SidePanel.Footer>
        <Button
          accessibilityLabel={i18n.t('sales_portal.edit_account.save')}
          disabled={saveDisabled}
          loading={accountDataSaving}
          onPress={handleSave}
          size="$md"
          variant="primary"
          width="100%"
        >
          {i18n.t('sales_portal.edit_account.save')}
        </Button>
      </SidePanel.Footer>
    </>
  );
};
